/**
 * TULIA CUSTOM — GA4 Behavior Tracking via GTM dataLayer
 *
 * Pushes custom events to dataLayer for GTM to forward to GA4.
 * All event definitions match the taxonomy in:
 *   OneDrive/Tulia Scrubs/Analytics/ga4_behavior_tracking_spec.md
 *
 * GTM container: GTM-NXP7PWNQ
 * GA4 property: 528269538 / G-7YBJDSCGYT
 */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // --- Utilities ---

  function push(eventName, params) {
    window.dataLayer.push(Object.assign({ event: eventName }, params));
  }

  function getPageType() {
    const path = location.pathname;
    if (path === '/') return 'homepage';
    if (path.startsWith('/products/')) return 'product';
    if (path.startsWith('/collections/')) return 'collection';
    if (path === '/cart') return 'cart';
    if (path === '/search') return 'search_results';

    // Content pages — match by template or path
    const templates = {
      '/pages/heroes-discount': 'content_heroes_discount',
      '/pages/faq': 'content_faq',
      '/pages/fabric-care': 'content_fabric_care',
      '/pages/about': 'content_about',
      '/pages/group-orders': 'content_group_orders',
      '/pages/contact': 'content_contact',
      '/pages/refer-a-friend': 'content_refer_a_friend',
      '/pages/wishlist': 'content_wishlist',
      '/pages/accessibility': 'content_accessibility',
    };
    for (const [prefix, type] of Object.entries(templates)) {
      if (path.startsWith(prefix)) return type;
    }
    if (path.startsWith('/pages/')) return 'content_other';
    if (path.startsWith('/blogs/')) return 'blog';
    if (path.startsWith('/account')) return 'account';
    return 'other';
  }

  // Push page_type on every page load so GTM variables can read it
  push('traffic_source_context', {
    page_type: getPageType(),
    referrer: document.referrer,
  });

  // Debounce helper
  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), ms);
    };
  }

  // --- UNIVERSAL: Time on Page ---
  [15, 30, 60, 120, 300].forEach(function (secs) {
    setTimeout(function () {
      push('time_on_page', {
        time_seconds: secs,
        page_type: getPageType(),
      });
    }, secs * 1000);
  });

  // Note: Scroll depth is handled by GTM's built-in scroll trigger — no JS needed here.

  // --- UNIVERSAL: Exit Navigation ---
  // Track where users go when they click internal links
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript')) return;

    // Only track internal navigation
    try {
      var url = new URL(href, location.origin);
      if (url.origin !== location.origin) return;
    } catch (_) {
      return;
    }

    var dest = url.pathname;
    var exitType = 'internal_content';
    if (dest.startsWith('/products/')) exitType = 'internal_product';
    else if (dest.startsWith('/collections/')) exitType = 'internal_collection';
    else if (dest === '/cart' || dest.startsWith('/checkout')) exitType = 'internal_cart';

    push('exit_navigation', {
      from_page_path: location.pathname,
      to_page_path: dest,
      exit_type: exitType,
    });
  });

  // Track bounces (leaving the site)
  window.addEventListener('beforeunload', function () {
    // Check if last navigation was internal — if not, it's a bounce
    push('exit_navigation', {
      from_page_path: location.pathname,
      to_page_path: '',
      exit_type: 'bounce',
    });
  });


  // ============================================================
  // HOMEPAGE
  // ============================================================
  if (getPageType() === 'homepage') {
    // Section visibility via IntersectionObserver
    var homeSections = document.querySelectorAll('main .shopify-section');
    if (homeSections.length && 'IntersectionObserver' in window) {
      var visibleSections = new Set();
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var el = entry.target;
            var id = el.id || el.className || 'unknown';
            if (visibleSections.has(id)) return;
            visibleSections.add(id);

            // Try to determine section name from classes or id
            var name = 'unknown';
            if (id.includes('banner') || id.includes('hero') || id.includes('slideshow')) name = 'hero_banner';
            else if (id.includes('featured') || id.includes('collection')) name = 'featured_collection';
            else if (id.includes('review')) name = 'reviews';
            else if (id.includes('faq')) name = 'faq';
            else if (id.includes('icon') || id.includes('why')) name = 'trust_features';
            else if (id.includes('newsletter') || id.includes('email')) name = 'newsletter';
            else name = id;

            push('homepage_section_visible', { section_id: id, section_name: name });
            sectionObserver.unobserve(el);
          }
        });
      }, { threshold: 0.3 });

      homeSections.forEach(function (s) { sectionObserver.observe(s); });
    }

    // Product clicks from featured collection
    document.addEventListener('click', function (e) {
      var card = e.target.closest('.tulia-product-card, .card--product, product-card');
      if (!card) return;
      var link = card.querySelector('a[href*="/products/"]');
      var title = card.querySelector('.card__heading, .tulia-product-card__title, .card-information__text');
      if (!link) return;

      // Find grid position
      var grid = card.closest('.grid, .collection-product-list, .featured-collection');
      var position = grid ? Array.from(grid.children).indexOf(card.closest('.grid__item, li')) + 1 : 0;

      push('homepage_product_click', {
        product_title: title ? title.textContent.trim() : '',
        product_id: '',
        position_in_grid: position,
      });
    });

    // Hero/banner CTA clicks
    document.addEventListener('click', function (e) {
      var cta = e.target.closest('.banner__button, .slideshow__button, .tulia-banner__cta, .button--primary');
      if (!cta) return;
      var section = cta.closest('.shopify-section');
      if (!section) return;
      var sectionId = section.id || '';
      if (!sectionId.includes('banner') && !sectionId.includes('hero') && !sectionId.includes('slideshow')) return;

      push('homepage_hero_interaction', {
        action: 'cta_click',
        slide_index: 0,
        target_url: cta.getAttribute('href') || '',
      });
    });
  }


  // ============================================================
  // PRODUCT PAGES
  // ============================================================
  if (getPageType() === 'product') {
    var productMeta = {
      id: '',
      title: '',
    };

    // Try to get product info from the page
    var productTitle = document.querySelector('.product__title h1, .product__title');
    if (productTitle) productMeta.title = productTitle.textContent.trim();

    // Get product ID from form
    var productForm = document.querySelector('form[action="/cart/add"]');
    if (productForm) {
      var idInput = productForm.querySelector('input[name="id"]');
      if (idInput) productMeta.id = idInput.value;
    }

    // Track total images
    var mediaGallery = document.querySelector('.product__media-gallery, .product--media, media-gallery');
    var allImages = mediaGallery ? mediaGallery.querySelectorAll('.product__media, .media-gallery__item, li') : [];
    var totalImages = allImages.length || 1;
    var viewedImages = new Set([0]); // First image is always viewed

    // Image gallery engagement — thumbnail clicks and gallery scrolls
    if (mediaGallery) {
      mediaGallery.addEventListener('click', function (e) {
        var thumb = e.target.closest('.thumbnail, .product__media-toggle, button, [data-media-id]');
        if (!thumb) return;
        var idx = Array.from(allImages).indexOf(thumb.closest('li, .product__media')) ;
        if (idx === -1) idx = Array.from(mediaGallery.querySelectorAll('button, .thumbnail')).indexOf(thumb);
        viewedImages.add(idx);

        push('product_image_view', {
          product_id: productMeta.id,
          product_title: productMeta.title,
          image_index: idx,
          total_images: totalImages,
          interaction_type: 'thumbnail',
        });
      });

      // Scroll-based image viewing (for mobile swipe galleries)
      var scrollObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var idx = Array.from(allImages).indexOf(entry.target);
            if (idx >= 0 && !viewedImages.has(idx)) {
              viewedImages.add(idx);
              push('product_image_view', {
                product_id: productMeta.id,
                product_title: productMeta.title,
                image_index: idx,
                total_images: totalImages,
                interaction_type: 'swipe',
              });
            }
          }
        });
      }, { threshold: 0.5 });
      allImages.forEach(function (img) { scrollObserver.observe(img); });
    }

    // Variant selection — subscribe to Dawn's PubSub
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function (data) {
        var target = data.data.target;
        if (!target) return;

        // Determine variant type (size vs color) from the fieldset/select label
        var fieldset = target.closest('fieldset, .product-form__input');
        var legend = fieldset ? fieldset.querySelector('legend, .form__label, label') : null;
        var variantType = 'unknown';
        if (legend) {
          var labelText = legend.textContent.toLowerCase().trim();
          if (labelText.includes('size')) variantType = 'size';
          else if (labelText.includes('color') || labelText.includes('colour')) variantType = 'color';
          else variantType = labelText;
        }

        var value = target.value || target.textContent || '';
        var isAvailable = !target.classList.contains('disabled') && !target.disabled;

        push('product_variant_select', {
          product_id: productMeta.id,
          product_title: productMeta.title,
          variant_type: variantType,
          variant_value: value.trim(),
          variant_available: isAvailable,
        });
      });
    }

    // Accordion opens (details/summary elements)
    document.querySelectorAll('.product__accordion details, .accordion details').forEach(function (detail) {
      detail.addEventListener('toggle', function () {
        if (!detail.open) return;
        var summary = detail.querySelector('summary .accordion__title, summary span, summary');
        var sectionName = summary ? summary.textContent.trim().toLowerCase().replace(/\s+/g, '_') : 'unknown';

        push('product_accordion_open', {
          product_id: productMeta.id,
          product_title: productMeta.title,
          accordion_section: sectionName,
        });
      });
    });

    // Size guide view
    document.addEventListener('click', function (e) {
      if (e.target.closest('.size-guide, [data-size-guide], .tulia-size-guide, a[href*="size"]')) {
        push('product_size_guide_view', {
          product_id: productMeta.id,
          product_title: productMeta.title,
        });
      }
    });

    // Related / cross-sell product clicks
    document.addEventListener('click', function (e) {
      var card = e.target.closest('.related-products .card, .complementary-products .card, .product-recommendations .card');
      if (!card) return;
      var link = card.querySelector('a[href*="/products/"]');
      var title = card.querySelector('.card__heading, .card-information__text');
      var section = card.closest('.related-products, .complementary-products, .product-recommendations');
      var sectionType = 'related';
      if (section && section.classList.contains('complementary-products')) sectionType = 'cross_sell';

      push('product_related_click', {
        product_id: productMeta.id,
        product_title: productMeta.title,
        clicked_product_id: '',
        clicked_product_title: title ? title.textContent.trim() : '',
        section_type: sectionType,
      });
    });

    // Product exit — fires on page unload with accumulated data
    var accordionsOpened = 0;
    var variantsSelected = 0;
    var pageLoadTime = Date.now();

    document.querySelectorAll('.product__accordion details, .accordion details').forEach(function (d) {
      d.addEventListener('toggle', function () { if (d.open) accordionsOpened++; });
    });
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () { variantsSelected++; });
    }

    window.addEventListener('beforeunload', function () {
      push('product_exit', {
        product_id: productMeta.id,
        product_title: productMeta.title,
        images_viewed: viewedImages.size,
        accordions_opened: accordionsOpened,
        variants_selected: variantsSelected,
        time_on_page_seconds: Math.round((Date.now() - pageLoadTime) / 1000),
        exit_destination: '', // GTM can't easily know where they're going on unload
      });
    });

    // Image depth — fire once with max images viewed (on unload)
    window.addEventListener('beforeunload', function () {
      push('product_image_depth', {
        product_id: productMeta.id,
        product_title: productMeta.title,
        images_viewed: viewedImages.size,
        total_images: totalImages,
      });
    });
  }


  // ============================================================
  // COLLECTION PAGES
  // ============================================================
  if (getPageType() === 'collection') {
    var collectionTitle = document.querySelector('.collection-hero__title, .title, h1');
    var collMeta = {
      id: location.pathname.split('/collections/')[1] || '',
      title: collectionTitle ? collectionTitle.textContent.trim() : '',
    };

    // Product click from grid
    document.addEventListener('click', function (e) {
      var card = e.target.closest('.tulia-product-card, .card--product, .product-card, .grid__item');
      if (!card) return;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var title = card.querySelector('.card__heading, .tulia-product-card__title, .card-information__text');
      var grid = card.closest('.product-grid, .collection-product-list, .grid');
      var items = grid ? grid.querySelectorAll('.grid__item, .tulia-product-card') : [];
      var position = Array.from(items).indexOf(card.closest('.grid__item') || card) + 1;

      push('collection_product_click', {
        collection_id: collMeta.id,
        collection_title: collMeta.title,
        product_id: '',
        product_title: title ? title.textContent.trim() : '',
        position_in_grid: position,
      });
    });

    // Sort usage
    var sortSelect = document.querySelector('.collection-sort select, #SortBy, select[name="sort_by"]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        push('collection_sort_used', {
          collection_id: collMeta.id,
          collection_title: collMeta.title,
          sort_value: sortSelect.value,
        });
      });
    }

    // Filter usage
    document.addEventListener('change', function (e) {
      var filter = e.target.closest('.facets__display input, .facet-checkbox, [name^="filter"]');
      if (!filter) return;
      var filterGroup = filter.closest('.disclosure-has-popup, .facets__disclosure, fieldset');
      var filterType = 'unknown';
      if (filterGroup) {
        var legend = filterGroup.querySelector('.facets__heading, legend, summary span');
        if (legend) filterType = legend.textContent.trim().toLowerCase();
      }

      push('collection_filter_used', {
        collection_id: collMeta.id,
        collection_title: collMeta.title,
        filter_type: filterType,
        filter_value: filter.value || filter.textContent || '',
      });
    });
  }


  // ============================================================
  // CART DRAWER
  // ============================================================
  (function () {
    var cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
    if (!cartDrawer) return;

    var drawerOpenTime = 0;

    // Observe cart drawer opening via attribute/class changes
    var drawerObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes') {
          var isOpen = cartDrawer.classList.contains('is-open') ||
                       cartDrawer.classList.contains('active') ||
                       cartDrawer.hasAttribute('open');
          if (isOpen && drawerOpenTime === 0) {
            drawerOpenTime = Date.now();
            // Count cart items and value
            var items = cartDrawer.querySelectorAll('.cart-item, .cart-drawer__item');
            var totalEl = cartDrawer.querySelector('.totals__subtotal-value, .cart-drawer__total .money, [data-cart-total]');
            push('cart_drawer_open', {
              cart_item_count: items.length,
              cart_total_value: totalEl ? totalEl.textContent.trim() : '',
            });
          } else if (!isOpen && drawerOpenTime > 0) {
            // Drawer closed
            var items = cartDrawer.querySelectorAll('.cart-item, .cart-drawer__item');
            var totalEl = cartDrawer.querySelector('.totals__subtotal-value, .cart-drawer__total .money, [data-cart-total]');
            push('cart_exit', {
              cart_item_count: items.length,
              cart_total_value: totalEl ? totalEl.textContent.trim() : '',
              exit_action: 'close_drawer',
            });
            drawerOpenTime = 0;
          }
        }
      });
    });
    drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class', 'open'] });

    // Quantity changes — subscribe to Dawn PubSub
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, function (data) {
        // Dawn fires this on quantity changes in the cart
        push('cart_quantity_change', {
          product_id: '',
          product_title: '',
          variant_title: '',
          quantity_action: 'change',
          old_quantity: '',
          new_quantity: '',
        });
      });
    }

    // Checkout button click
    document.addEventListener('click', function (e) {
      var checkoutBtn = e.target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"]');
      if (!checkoutBtn) return;
      var items = cartDrawer.querySelectorAll('.cart-item, .cart-drawer__item');
      var totalEl = cartDrawer.querySelector('.totals__subtotal-value, .cart-drawer__total .money, [data-cart-total]');
      push('cart_checkout_context', {
        cart_item_count: items.length,
        cart_total_value: totalEl ? totalEl.textContent.trim() : '',
        time_in_cart_seconds: drawerOpenTime > 0 ? Math.round((Date.now() - drawerOpenTime) / 1000) : 0,
        discount_applied: !!cartDrawer.querySelector('.cart-discount, [data-discount]'),
      });
    });

    // Discount code field
    document.addEventListener('focus', function (e) {
      if (e.target.matches('[name="discount"], .cart__discount input, input[placeholder*="iscount"]')) {
        push('cart_discount_code_attempt', {
          discount_action: 'focus',
          code_entered: false,
        });
      }
    }, true);
  })();


  // ============================================================
  // FAQ PAGE
  // ============================================================
  if (getPageType() === 'content_faq') {
    var faqOpenCount = 0;
    var totalFaqItems = document.querySelectorAll('.tulia-faq-page__item').length;

    // Question open
    document.querySelectorAll('.tulia-faq-page__item').forEach(function (item, idx) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        faqOpenCount++;
        var qText = item.querySelector('.tulia-faq-page__question span');
        push('faq_question_open', {
          question_text: qText ? qText.textContent.trim() : '',
          question_index: idx,
          questions_opened_count: faqOpenCount,
        });
      });
    });

    // FAQ depth — fire on unload
    window.addEventListener('beforeunload', function () {
      if (faqOpenCount > 0) {
        push('faq_questions_depth', {
          total_opened: faqOpenCount,
          total_available: totalFaqItems,
        });
      }
    });

    // FAQ to contact
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/contact"], a[href*="contact-us"]');
      if (!link) return;
      // Find the last opened FAQ
      var openItems = document.querySelectorAll('.tulia-faq-page__item[open]');
      var lastQ = '';
      if (openItems.length) {
        var lastItem = openItems[openItems.length - 1];
        var span = lastItem.querySelector('.tulia-faq-page__question span');
        lastQ = span ? span.textContent.trim() : '';
      }
      push('faq_to_contact', { last_question_viewed: lastQ });
    });

    // FAQ search
    var faqSearchInput = document.querySelector('.tulia-faq-page__search input');
    if (faqSearchInput) {
      var lastSearchTerm = '';
      faqSearchInput.addEventListener('input', debounce(function () {
        var term = faqSearchInput.value.trim();
        if (term.length < 2 || term === lastSearchTerm) return;
        lastSearchTerm = term;

        // Count visible results
        var visibleItems = document.querySelectorAll('.tulia-faq-page__item:not([style*="display: none"])');
        var noResults = document.querySelector('.tulia-faq-page__no-results');
        var resultCount = (noResults && noResults.style.display !== 'none') ? 0 : visibleItems.length;

        push('faq_search_query', {
          search_term: term,
          results_count: resultCount,
        });

        if (resultCount === 0) {
          push('faq_search_no_results', { search_term: term });
        }
      }, 500));
    }
  }


  // ============================================================
  // HEROES DISCOUNT PAGE
  // ============================================================
  if (getPageType() === 'content_heroes_discount') {
    // CTA clicks
    document.addEventListener('click', function (e) {
      var cta = e.target.closest('a.button, .button--primary, a[href*="/collections"]');
      if (!cta) return;
      push('heroes_discount_code_interaction', {
        action: 'cta_click',
        target_url: cta.getAttribute('href') || '',
      });
    });

    // Section visibility
    var heroSections = document.querySelectorAll('.shopify-section .tulia-heroes-discount > *, .page-content > *');
    if (heroSections.length && 'IntersectionObserver' in window) {
      var heroVisObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            push('heroes_eligibility_view', {
              section_name: entry.target.className || 'section',
            });
            heroVisObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
      heroSections.forEach(function (s) { heroVisObs.observe(s); });
    }
  }


  // ============================================================
  // FABRIC CARE PAGE
  // ============================================================
  if (getPageType() === 'content_fabric_care') {
    document.querySelectorAll('details').forEach(function (detail) {
      detail.addEventListener('toggle', function () {
        if (!detail.open) return;
        var summary = detail.querySelector('summary');
        push('fabric_care_section_expand', {
          section_name: summary ? summary.textContent.trim() : 'unknown',
        });
      });
    });
  }


  // ============================================================
  // FORM PAGES (Group Orders, Contact)
  // ============================================================
  if (getPageType() === 'content_group_orders' || getPageType() === 'content_contact') {
    var prefix = getPageType() === 'content_group_orders' ? 'group_orders' : 'contact';
    var touchedFields = new Set();

    // Track field interactions
    document.addEventListener('focus', function (e) {
      var input = e.target.closest('input, textarea, select');
      if (!input) return;
      var form = input.closest('form');
      if (!form) return;
      var fieldName = input.name || input.id || input.placeholder || 'unknown';
      if (touchedFields.has(fieldName)) return;
      touchedFields.add(fieldName);

      push(prefix + '_form_field_interact', { field_name: fieldName });
    }, true);

    // Form submit
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form');
      if (!form) return;
      push(prefix + '_form_submit', { fields_completed: touchedFields.size });
    });

    // Form abandon (on unload with partially filled form)
    window.addEventListener('beforeunload', function () {
      if (touchedFields.size > 0) {
        var allFields = document.querySelectorAll('form input, form textarea, form select');
        var allNames = Array.from(allFields).map(function (f) { return f.name || f.id || 'unknown'; });
        var skipped = allNames.filter(function (n) { return !touchedFields.has(n); });
        push(prefix + '_form_abandon', {
          fields_completed: Array.from(touchedFields).join(','),
          fields_skipped: skipped.join(','),
        });
      }
    });
  }

  // Group orders pricing section visibility
  if (getPageType() === 'content_group_orders') {
    var pricingSections = document.querySelectorAll('[class*="pricing"], [class*="quantity"], details');
    if (pricingSections.length && 'IntersectionObserver' in window) {
      var pricingObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            push('group_orders_pricing_view', {
              section_name: entry.target.className || 'pricing',
            });
            pricingObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
      pricingSections.forEach(function (s) { pricingObs.observe(s); });
    }
  }


  // ============================================================
  // REFER A FRIEND
  // ============================================================
  if (getPageType() === 'content_refer_a_friend') {
    // Track share/referral interactions
    document.addEventListener('click', function (e) {
      var shareBtn = e.target.closest('.share-button, [data-share], button[class*="refer"], a[class*="share"]');
      if (!shareBtn) return;
      push('referral_flow_start', {});
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form');
      if (!form) return;
      push('referral_flow_complete', { share_method: 'form' });
    });

    // Copy link click
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-copy], .copy-link, button[class*="copy"]')) {
        push('referral_flow_complete', { share_method: 'link_copy' });
      }
    });
  }


  // ============================================================
  // WISHLIST
  // ============================================================
  // tulia-wishlist.js fires tulia:favorites:changed after each heart tap, saying whether
  // it added or removed the product
  document.addEventListener('tulia:favorites:changed', function (e) {
    var detail = e.detail || {};
    var wishBtn = detail.button;
    var card = wishBtn ? wishBtn.closest('.tulia-card, .tulia-product-card, .product-card, .grid__item') : null;
    // Product cards link the image before the title, so look for the title class first
    var titleEl = card ? (card.querySelector('.tulia-card__title') ||
      card.querySelector('.card__heading, .tulia-product-card__title, a[href*="/products/"]')) : null;
    var title = titleEl ? titleEl.textContent.trim() : '';

    push(detail.action === 'remove' ? 'wishlist_item_remove' : 'wishlist_item_add', {
      product_id: detail.productId ? String(detail.productId) : '',
      product_title: title,
    });
  });


  // ============================================================
  // SITE SEARCH (Global)
  // ============================================================
  (function () {
    // Predictive search clicks
    document.addEventListener('click', function (e) {
      var resultLink = e.target.closest('.predictive-search__result-group a, predictive-search a');
      if (!resultLink) return;

      var searchInput = document.querySelector('.search__input, input[name="q"]');
      var resultItem = resultLink.closest('.predictive-search__result-group li, .predictive-search__item');
      var list = resultItem ? resultItem.closest('ul, .predictive-search__results-list') : null;
      var position = (list && resultItem) ? Array.from(list.children).indexOf(resultItem) + 1 : 0;

      push('predictive_search_click', {
        typed_text: searchInput ? searchInput.value.trim() : '',
        suggestion_clicked: resultLink.textContent.trim().substring(0, 100),
        suggestion_position: position,
      });
    });

    // Full search submission
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action="/search"]');
      if (!form) return;
      var input = form.querySelector('input[name="q"]');
      if (!input) return;

      push('site_search_query', {
        search_term: input.value.trim(),
        results_count: -1, // We don't know results count at submission time
      });
    });

    // Search results page — track result clicks and no-results
    if (getPageType() === 'search_results') {
      // Check for no results
      var noResults = document.querySelector('.template-search__header .h1');
      var searchInput = document.querySelector('input[name="q"]');
      var searchTerm = searchInput ? searchInput.value.trim() : new URLSearchParams(location.search).get('q') || '';

      if (noResults && noResults.textContent.includes('0')) {
        push('site_search_no_results', { search_term: searchTerm });
      }

      // Result clicks
      document.addEventListener('click', function (e) {
        var resultLink = e.target.closest('.search__results a, .template-search a[href*="/products/"], .template-search a[href*="/pages/"]');
        if (!resultLink) return;
        var list = resultLink.closest('ul, .search__results');
        var item = resultLink.closest('li, .card');
        var position = (list && item) ? Array.from(list.children).indexOf(item) + 1 : 0;

        var type = 'other';
        var href = resultLink.getAttribute('href') || '';
        if (href.includes('/products/')) type = 'product';
        else if (href.includes('/pages/')) type = 'page';
        else if (href.includes('/blogs/')) type = 'article';

        push('site_search_result_click', {
          search_term: searchTerm,
          clicked_item_title: resultLink.textContent.trim().substring(0, 100),
          clicked_item_type: type,
          result_position: position,
        });
      });
    }
  })();

})();
